import { Component, Element, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { isMobile } from "../../utils/screen-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { detectSlotChanges } from "../../utils/slot-helper";
import { getNestedChildrenOf } from "../../utils/dom-helper";
import { scrollToElement } from "../../utils/scroll-helper";
import webComponentsDebug from "../../utils/log-helper";

// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 * @slot preloader - place desired preloader component here.
 */
@Component({
  tag: "eon-ui-list",
  styleUrl: "list.scss",
  shadow: true
})
export class List {
  // @InitLogging() sentryTransaction;

  @Element() hostElement: HTMLEonUiListElement;
  @State() isMobile: boolean;
  listItems: Element[] = [];
  @State() visibleListItems: Element[] = [];
  @State() loading: boolean = false;
  @State() scrolling: boolean = false;
  @State() blockScrolling: boolean;
  @State() showPagination: boolean = true;

  pageNumberInput: HTMLEonUiInputElement;
  listElement: HTMLElement;

  /**
   * The Items to be viewed per page
   */
  @State() pageSize: number;

  /**
   * Comma seperated list of page sizes
   */
  @Prop() pageSizes: string = "5,10,20,50,100";

  /**
   * Initial page size
   */
  @Prop({ mutable: true }) initialPageSize: number;

  /**
   * represents the current page index
   */
  @Prop({ mutable: true, reflect: true }) currentPage: number = 1;

  /**
   * Provide to total number of items. Use this if you want to use this component with a backend api with paging.
   */
  @Prop() totalItems: number;

  /**
   * Disables client side paging. Use this if your paging is handled in the backend. This property disables the client side paging and always shows all child items
   */
  @Prop() serverMode: boolean = false;

  /**
   * Select type of pagination
   */
  @Prop() paginationType: "dropdown" | "numbers" = "dropdown";

  private get sortedPageSizeArray(): number[] {
    return this.pageSizes
      .split(",")
      .map((num) => parseInt(num))
      .sort((a, b) => {
        return a - b;
      });
  }

  private get numberOfPages(): number {
    const totalItems = this.totalItems ?? this.listItems.length;
    return Math.ceil(Number(totalItems) / this.pageSize);
  }

  private get listLength(): number {
    return this.totalItems ?? this.listItems.length;
  }

  @Listen("resize", { target: "window" })
  onViewportSizeChange() {
    this.isMobile = isMobile();
  }

  componentWillLoad() {
    this.storeItemsInArray();
    this.verifyInitialPageSize();
    this.getVisibleItems();
    this.onViewportSizeChange();
    this.hidePaginationWrapper();

    detectSlotChanges(() => {
      this.storeItemsInArray();
      this.getVisibleItems();
      this.hidePaginationWrapper();
    }, this.hostElement);
  }

  storeItemsInArray() {
    this.listItems = Array.from(
      getNestedChildrenOf(this.hostElement, "eon-ui-list-item") as HTMLCollectionOf<HTMLEonUiListItemElement>
    );
  }

  @Watch("pageSize")
  updateListItems() {
    this.setCurrentPage(this.currentPage);
  }

  @Watch("currentPage")
  @Watch("pageSize")
  getVisibleItems() {
    if (webComponentsDebug) {
      console.log(
        "Watch currentPage && pageSize ==> ",
        "initialPageSize:",
        this.initialPageSize,
        "pageSize:",
        this.pageSize,
        "currentPage:",
        this.currentPage
      );
    }
    const begin = (this.currentPage - 1) * this.pageSize;
    const end = begin + this.pageSize > this.listLength ? this.listLength : begin + this.pageSize;

    if (!this.serverMode) {
      const showNextPage = () => {
        this.listItems.forEach((listItem) => {
          listItem.removeAttribute("visible");
        });

        this.visibleListItems = this.listItems.slice(begin, end);
        this.visibleListItems.forEach((visibleListItem) => {
          visibleListItem.setAttribute("visible", "true");
        });
      };

      showNextPage();
    } else {
      this.visibleListItems = this.listItems;
      this.visibleListItems.forEach((visibleListItem) => {
        visibleListItem.setAttribute("visible", "true");
      });
    }
  }

  /**
   * Method to reinitiate/reload the page, e.g. listElement.reInitiateList();
   */
  @Method()
  async reInitiateList(): Promise<void> {
    this.currentPage = 1;
  }

  private verifyInitialPageSize() {
    if (!this.initialPageSize || !this.pageSizes.includes(this.initialPageSize.toString())) {
      this.initialPageSize = this.sortedPageSizeArray[0];
    }

    this.blockScrolling = true; // initial block
    this.pageSize = this.initialPageSize;
  }

  private async goToTop() {
    this.scrolling = true;
    await scrollToElement(this.hostElement, {
      scrollContainer: window,
      offset: -200
    });
    this.scrolling = false;
  }

  private setCurrentPage(page: number) {
    if (page > this.numberOfPages) {
      this.currentPage = this.numberOfPages;
    } else {
      this.currentPage = page;
    }

    if (page < 1) {
      this.currentPage = 1;
    }

    // Check for initial scroll block
    if (this.blockScrolling) {
      this.blockScrolling = false;
      return;
    }

    this.goToTop();
  }

  @Watch("totalItems")
  @Watch("pageSizes")
  hidePaginationWrapper() {
    if (this.listLength <= this.sortedPageSizeArray[0]) {
      this.showPagination = false;
    } else {
      this.showPagination = true;
    }
  }

  render() {
    return (
      <Host>
        <div class={pfx({ "list-wrapper": true, loading: this.loading || this.scrolling })}>
          <div ref={(element) => (this.listElement = element)} class={pfx({ list: true })}>
            <slot />
          </div>
          {this.showPagination && (
            <eon-ui-pagination
              paginationType={this.paginationType}
              totalItems={this.listLength}
              initialPageSize={this.initialPageSize}
              pageSize={this.pageSize}
              pageSizes={this.pageSizes}
              currentPage={this.currentPage}
              onPageChanged={(event) => this.setCurrentPage(event.detail)}
              onPageIncreased={(event) => this.setCurrentPage(event.detail)}
              onPageDecreased={(event) => this.setCurrentPage(event.detail)}
              onPageSizeChanged={(event) => (this.pageSize = event.detail)}
            />
          )}
          <div class={pfx({ "preloading-overlay": true })}>
            <slot name="preloader" />
          </div>
        </div>
      </Host>
    );
  }
}
